/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import {
  AuthenticatedUser,
  JwtRefreshResponse,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
} from "Api";
import axios from "axios";
import jwtDecode from "jwt-decode";
import { notifyGeneralError } from "../Shared/Errors";
import { Endpoints } from "./Common";

export interface DecodedAuthToken {
  aud: string;
  exp: number;
  iss: string;
}

function scheduleTask(
  millisFromNow: number,
  doTask: () => Promise<void>
): () => void {
  const renewTask = window.setTimeout(() => {
    doTask();
  }, millisFromNow);

  return () => {
    window.clearTimeout(renewTask);
  };
}

function getNewAuthToken(refreshResponse?: JwtRefreshResponse): string {
  if (!refreshResponse) {
    throw Error("Refresh response was empty");
  }

  if (refreshResponse.errors) {
    throw Error(
      `Errors occurred on the server while refreshing; ${refreshResponse.errors.toString()}`
    );
  }
  return refreshResponse.authToken;
}

export class DexterAuth {
  public constructor(
    private _user: AuthenticatedUser,
    private _authorized: boolean,
    private onUpdateUser?: (newUser: AuthenticatedUser) => void,
    private onUpdateAuthToken?: (
      newAuthToken: string,
      expiresAtUtcMillis?: number
    ) => void
  ) {}

  public get user(): AuthenticatedUser {
    return this._user;
  }

  /**
   * @returns true if there's no user data in local storage.
   */
  public isAuthorized(): boolean {
    return this._authorized && !this.user.mustChangePassword;
  }

  public async refreshAuthToken(): Promise<void> {
    const cancelRefreshToken = axios.CancelToken.source();
    try {
      // No axiosContext is needed here; authorization read from
      // httpOnly cookie on the server side.
      const postResponse = await axios.get(Endpoints.GetRefreshToken, {
        cancelToken: cancelRefreshToken.token,
      });

      const newAuthToken = getNewAuthToken(postResponse?.data);
      this.handleNewToken(newAuthToken);
    } catch (error) {
      notifyGeneralError(error, "Refresh unsuccessful");
    }
  }

  private handleNewToken(
    newAuthToken?: string,
    scheduleRenewal?: boolean
  ): void {
    let decodedNewToken: DecodedAuthToken | null = null;

    if (newAuthToken) {
      decodedNewToken = jwtDecode<DecodedAuthToken>(newAuthToken);
    }

    let expiresAtUtcMillis: number | null = null;
    if (decodedNewToken) {
      expiresAtUtcMillis = decodedNewToken.exp * 1000;
    }

    if (this.onUpdateAuthToken) {
      this.onUpdateAuthToken(newAuthToken, expiresAtUtcMillis);
    }

    if (scheduleRenewal && expiresAtUtcMillis) {
      this.scheduleRenewal(expiresAtUtcMillis);
    }
  }

  private scheduleRenewal(expiresAtUtcMillis: number): void {
    const remainingTime = expiresAtUtcMillis - Date.now();

    // Refresh with 10% of our allotted time remaining to allow for
    // server communication issues. To make sure we don't run into
    // server time drift issues, make sure this value doesn't fall
    // below 10 seconds of the expected expiration time.
    const safeRefreshWindow = Math.max(remainingTime * 0.1, 10000);

    // Determine when to do the refresh, but never make it more frequent than
    // every 10 seconds.
    const millisFromNowToRefresh = Math.max(
      remainingTime - safeRefreshWindow,
      10000
    );
    scheduleTask(millisFromNowToRefresh, () => this.refreshAuthToken());
  }

  /**
   * Log the user in via their identifier and optional password.
   */
  public async loginUser(username: string, password?: string): Promise<void> {
    const cancelLoginToken = axios.CancelToken.source();
    const loginRequest: LoginRequest = {
      username: username,
      password: password,
    };
    try {
      const postResponse = await axios.post(Endpoints.Login, loginRequest, {
        cancelToken: cancelLoginToken.token,
      });
      const loginResponse: LoginResponse = postResponse?.data;

      // The server returns 200 even on auth failure, with errors populated and
      // a null token. Treat that as a failed login.
      if (!loginResponse?.authToken) {
        notifyGeneralError(
          new Error("Invalid credentials"),
          "Login unsuccessful."
        );
        return;
      }

      this.handleNewToken(
        loginResponse.authToken,
        !loginResponse.user.mustChangePassword
      );

      if (this.onUpdateUser) {
        this.onUpdateUser(loginResponse.user);
      }
    } catch (error) {
      notifyGeneralError(error, "Login unsuccessful.");
    }
  }

  /**
   * Register a new user with a name, email, and password.
   * On success the caller can route the user to the login screen; on failure
   * the server error is surfaced via the existing notification path.
   */
  public async registerUser(
    name: string,
    email: string,
    password: string
  ): Promise<boolean> {
    const cancelRegisterToken = axios.CancelToken.source();
    const registerRequest: RegisterRequest = {
      name: name,
      email: email,
      password: password,
    };
    try {
      // No axiosContext is needed here; registering does not require an
      // authorization header.
      await axios.post(Endpoints.Register, registerRequest, {
        cancelToken: cancelRegisterToken.token,
      });
      return true;
    } catch (error) {
      notifyGeneralError(error, "Registration unsuccessful.");
      return false;
    }
  }

  /**
   * No local session state is cleared. Must be handled separately
   */
  public async logoutFromServer(): Promise<void> {
    try {
      await axios.post(Endpoints.LogOut);
      if (this.onUpdateUser) {
        this.onUpdateUser(undefined);
      }
      if (this.handleNewToken) {
        this.handleNewToken(undefined);
      }
    } catch (error) {
      notifyGeneralError(error, "Logout unsuccessful.");
    }
  }
}
